import React, { useState, useCallback, useRef, useMemo, useId } from 'react';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { usePublicCollection } from '../../hooks/usePublicCollection';
import { GALLERY } from '../../services/publicContent';
import { isSinglePhoto } from '../../services/galleryUtils';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { Layout } from '../layout/Layout';
import { Modal } from '../layout/Modal';
import { SEOHead } from '../SEOHead';
import { buildBreadcrumbSchema } from '../../utils/seo';
import { useLocalizedField } from '../../hooks/useLocalizedField';
import { IoCloseOutline } from 'react-icons/io5';

const GALLERY_IMAGE_WIDTH = 1200;
const GALLERY_IMAGE_HEIGHT = 900;
const galleryFrameStyle = {
    position: 'relative',
    aspectRatio: '4 / 3',
    overflow: 'hidden',
};
const galleryImageStyle = {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
};

export const Gallery = () => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const pick = useLocalizedField();
    const { items, loading, error } = usePublicCollection(GALLERY);
    const [activeCategoryIndex, setActiveCategoryIndex] = useState(-1); // -1 = "all"
    const [selectedItem, setSelectedItem] = useState(null);

    // SEO
    const seoTitle = t('seo.galleryTitle');
    const seoDescription = t('seo.galleryDescription');

    // Build stable category list from base (Ukrainian) keys — memoized
    const baseCategories = useMemo(
        () => [...new Set(items.map(item => item.category).filter(Boolean))],
        [items],
    );

    // Precompute UA→EN category map once per `items` change so per-button
    // lookup in getCategoryLabel is O(1) instead of O(items × categories).
    const categoryEnMap = useMemo(() => {
        const map = {};
        items.forEach(item => {
            if (item.category && !map[item.category] && item.categoryEn) {
                map[item.category] = item.categoryEn;
            }
        });
        return map;
    }, [items]);

    const getCategoryLabel = (baseCat) => {
        if (language === 'en') return categoryEnMap[baseCat] || baseCat;
        return baseCat;
    };

    const safeIndex = baseCategories.length > 0
        ? Math.min(activeCategoryIndex, baseCategories.length - 1)
        : -1;
    const activeBaseCategory = safeIndex >= 0 ? baseCategories[safeIndex] : null;

    const filtered = useMemo(
        () => activeBaseCategory
            ? items.filter(item => item.category === activeBaseCategory)
            : items,
        [items, activeBaseCategory],
    );

    // Preload images on hover/touch so the lightbox opens instantly
    const preloadedUrls = useRef(new Set());
    const preloadImages = useCallback((item) => {
        [item.beforeImage, item.afterImage].filter(Boolean).forEach(url => {
            if (!preloadedUrls.current.has(url)) {
                preloadedUrls.current.add(url);
                const img = new Image();
                img.src = url;
            }
        });
    }, []);

    const lightboxTitleId = useId();
    const renderItemInfo = (item) => (
        <div className="p-4 sm:p-5">
            {item.category && (
                <span className="inline-block px-2.5 py-0.5 bg-gold/10 text-gold text-xs font-semibold rounded-full mb-2">
                    {pick(item, 'category')}
                </span>
            )}
            {(item.description || item.descriptionEn) && (
                <p className="text-sm text-gray-600 leading-relaxed">
                    {pick(item, 'description')}
                </p>
            )}
        </div>
    );

    return (
        <Layout className="min-h-screen flex flex-col overflow-x-hidden">
            <SEOHead
                title={seoTitle}
                description={seoDescription}
                path="/gallery"
                schema={buildBreadcrumbSchema([
                    { name: t('nav.home'), path: '/' },
                    { name: t('nav.gallery'), path: '/gallery' },
                ])}
            />

            {/* Page header */}
            <section className="bg-navy py-12 sm:py-16 md:py-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
                        {t('gallery.title')}
                    </h1>
                    <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto">
                        {t('gallery.subtitle')}
                    </p>
                </div>
            </section>

            {/* Gallery content */}
            <div className="py-10 sm:py-14 md:py-20 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="sr-only">{t('gallery.subtitle')}</h2>
                    {loading ? (
                        <div className="flex justify-center items-center min-h-[60vh]">
                            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center py-20">
                            <p className="text-gray-600">
                                {t('errors.gallery')}
                            </p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-700 mb-2">{t('gallery.empty')}</h3>
                            <p className="text-gray-500">{t('gallery.emptyDesc')}</p>
                        </div>
                    ) : (
                        <>
                            {/* Category filter tabs */}
                            {baseCategories.length > 1 && (
                                <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
                                    <button
                                        type="button"
                                        onClick={() => setActiveCategoryIndex(-1)}
                                        className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-gold/40 ${
                                            safeIndex === -1
                                                ? 'bg-gold text-white shadow-md'
                                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                        }`}
                                    >
                                        {t('gallery.allCategories')}
                                    </button>
                                    {baseCategories.map((baseCat, idx) => (
                                        <button
                                            type="button"
                                            key={baseCat}
                                            onClick={() => setActiveCategoryIndex(idx)}
                                            className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-gold/40 ${
                                                safeIndex === idx
                                                    ? 'bg-gold text-white shadow-md'
                                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                            }`}
                                        >
                                            {getCategoryLabel(baseCat)}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Gallery grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
                                {filtered.map((item, index) => isSinglePhoto(item) ? (
                                    <button
                                        type="button"
                                        key={item.id}
                                        onClick={() => setSelectedItem(item)}
                                        onMouseEnter={() => preloadImages(item)}
                                        onTouchStart={() => preloadImages(item)}
                                        aria-label={pick(item, 'description') || t('gallery.title')}
                                        className="group bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 text-left focus:outline-none focus:ring-2 focus:ring-gold/60"
                                    >
                                        <div
                                            className="relative aspect-[4/3] overflow-hidden bg-gray-100"
                                            style={galleryFrameStyle}
                                        >
                                            <img
                                                src={item.beforeThumb || item.beforeImage}
                                                alt={pick(item, 'description') || 'Gallery photo'}
                                                className="w-full h-full object-cover object-center"
                                                width={GALLERY_IMAGE_WIDTH}
                                                height={GALLERY_IMAGE_HEIGHT}
                                                style={galleryImageStyle}
                                                draggable={false}
                                                loading={index < 3 ? 'eager' : 'lazy'}
                                                decoding="async"
                                                fetchPriority={index < 3 ? 'high' : 'auto'}
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors z-20 flex items-center justify-center">
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                                                    <svg className="w-6 h-6 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="11" cy="11" r="8" />
                                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                        <line x1="11" y1="8" x2="11" y2="14" />
                                                        <line x1="8" y1="11" x2="14" y2="11" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                        {renderItemInfo(item)}
                                    </button>
                                ) : (
                                    <div
                                        key={item.id}
                                        onMouseEnter={() => preloadImages(item)}
                                        onTouchStart={() => preloadImages(item)}
                                        className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 text-left"
                                    >
                                        <BeforeAfterSlider
                                            beforeImage={item.beforeImage}
                                            afterImage={item.afterImage}
                                            beforePreviewImage={item.beforeThumb}
                                            afterPreviewImage={item.afterThumb}
                                            beforeLabel={t('gallery.before')}
                                            afterLabel={t('gallery.after')}
                                            dragHint={t('gallery.dragHint')}
                                            compact
                                            loading={index < 3 ? 'eager' : 'lazy'}
                                            className="rounded-none"
                                        />
                                        {renderItemInfo(item)}
                                    </div>
                                ))}
                            </div>

                            {/* Instruction text */}
                            <p className="text-center text-sm text-gray-500 mt-10 max-w-2xl mx-auto">
                                {t('gallery.instruction')}
                            </p>
                        </>
                    )}
                </div>
            </div>

            {/* Lightbox modal */}
            <Modal
                open={Boolean(selectedItem)}
                onClose={() => setSelectedItem(null)}
                labelledBy={lightboxTitleId}
                className={selectedItem && isSinglePhoto(selectedItem)
                    ? 'max-w-[90vw] sm:max-w-3xl'
                    : 'max-w-3xl'}
            >
                {selectedItem && (
                    <div className="relative flex flex-col">
                        <h2 id={lightboxTitleId} className="sr-only">
                            {pick(selectedItem, 'description') || t('gallery.title')}
                        </h2>
                        <button onClick={() => setSelectedItem(null)}
                            className="absolute top-3 right-3 z-30 p-2 bg-white/90 hover:bg-white rounded-full shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-gold/60"
                            aria-label="Close">
                            <IoCloseOutline size="24px" className="text-gray-700" />
                        </button>
                        <div className="p-3 sm:p-4 min-h-0 flex-1 flex items-center justify-center">
                            {isSinglePhoto(selectedItem) ? (
                                <img src={selectedItem.beforeImage}
                                    alt={pick(selectedItem, 'description') || 'Gallery photo'}
                                    className="max-w-full max-h-[calc(90vh-8rem)] rounded-xl"
                                    width={GALLERY_IMAGE_WIDTH}
                                    height={GALLERY_IMAGE_HEIGHT}
                                    style={{
                                        display: 'block',
                                        maxWidth: '100%',
                                        maxHeight: 'calc(90vh - 8rem)',
                                        width: 'auto',
                                        height: 'auto',
                                    }}
                                    decoding="async" />
                            ) : (
                                <BeforeAfterSlider
                                    beforeImage={selectedItem.beforeImage}
                                    afterImage={selectedItem.afterImage}
                                    beforeLabel={t('gallery.before')}
                                    afterLabel={t('gallery.after')}
                                    dragHint={t('gallery.dragHint')}
                                />
                            )}
                        </div>
                        <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex-shrink-0">
                            {selectedItem.category && (
                                <span className="inline-block px-3 py-1 bg-gold/10 text-gold text-xs font-semibold rounded-full mb-3">
                                    {pick(selectedItem, 'category')}
                                </span>
                            )}
                            {(selectedItem.description || selectedItem.descriptionEn) && (
                                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                    {pick(selectedItem, 'description')}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </Layout>
    );
};
